import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { SAMPLE_STATUS } from '@lims/shared-types';
import { samplesApi } from '../../api/endpoints.js';
import type { Sample, SampleWorkOrderGroup } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  CheckboxField,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  PaginationNav,
  Panel,
  SearchField,
  SearchSelectField,
  SegmentedControl,
  SelectField,
  StatusBadge,
  SwitchField,
  type DataColumn,
  type SelectOption,
} from '../../components/ui/index.js';
import { formatDate, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { StatusViews } from './StatusViews.js';
import { toneForSampleStatus } from './status.js';
import { useSampleTypes } from './sample-type-fields.js';
import { readOpenGroups, rememberOpenGroup } from './open-groups.js';

/**
 * Work orders per page (D321). Each carries up to `GROUP_SAMPLE_LIMIT` of its
 * samples, so a page is bounded however large one work order grows.
 */
const GROUPS_PER_PAGE = 10;
const GROUP_SAMPLE_LIMIT = 25;

/**
 * The sample register.
 *
 * ===========================================================================
 * THE SEARCH BOX IS THE BARCODE SCANNER. THERE IS NO SCANNER INTEGRATION.
 * ===========================================================================
 *
 * A keyboard-wedge scanner types the label's contents into whatever field has
 * focus, exactly as a person would, and the label's Code 128 carries the Sample
 * ID alone (decision D68). So a scan is a search, the search box is
 * autofocused, and the product assumes no hardware, no driver and no permission
 * to talk to a device. A laboratory with no scanner types the same identifier
 * and gets the same answer.
 *
 * WHAT THE STATUS COLUMN IS. It is the sample's acceptance state, derived by
 * the backend from the append-only decision history. This page renders it and
 * never recomputes it.
 *
 * DISPOSED SAMPLES ARE HIDDEN BY DEFAULT AND SAID TO BE HIDDEN. The record of a
 * disposed sample is kept forever — only the material is gone — so the filter
 * that reveals them is a visible control rather than a buried one. An empty
 * register after filtering says "nothing matched these filters", never "this
 * laboratory has no samples".
 *
 * EVERY SAMPLE LEADS BACK TO ITS RECEIPT (D213): the receipt number under the
 * Sample ID is a link, kept inside the same cell so a phone-width table gains
 * no column.
 *
 * VOIDED ("DELETED") SAMPLES ARE HIDDEN (D211). "Show deleted samples" is
 * offered only to a holder of `samples.void`, and the server refuses the
 * request to anyone else (D214); a voided row says so with its own chip.
 *
 * GROUPED BY WORK ORDER (D320). The server orders the page by work order —
 * newest first unless "oldest first" is chosen — and each work order's samples
 * newest first, with the samples linked to no work order last; this page only
 * cuts that order where the work order changes and never re-sorts it.
 *
 * PAGED BY WORK ORDER (D321). A page is `GROUPS_PER_PAGE` work orders, never a
 * number of samples, so a work order is never split across two pages. A group
 * with more than `GROUP_SAMPLE_LIMIT` matching samples shows that many and
 * offers the rest in the group itself ("Show more samples").
 *
 * THE WORK ORDER FILTER IS SEARCHED ON THE SERVER (D322): all, one work order,
 * or the samples with none. Typing narrows a short list of this laboratory's
 * work orders by number, client or project, so it scales to any number. The
 * choice lives in the address (`workOrderId`, `job`), which is also how
 * receiving's "View samples" opens the register on one work order.
 *
 * OPEN AND CLOSED GROUPS ARE REMEMBERED IN THIS BROWSER (D323), per person,
 * never in the database (`open-groups.ts`). With no choice remembered, the
 * first group of a page is open and the rest closed. While a search or one
 * work order is shown, every matching group opens so a scanned sample is never
 * hidden; a choice made then lasts for that result only.
 */
export function SamplesPage() {
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  // The work order filter (D322), in the address: one work order's id, or
  // `none`. `job` is its number, for display only, so a link from receiving
  // ("View samples", D197) shows the choice before any search has run.
  const [searchParams, setSearchParams] = useSearchParams();
  const workOrderFilter = searchParams.get('workOrderId');
  const workOrderLabel = searchParams.get('job');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [sampleType, setSampleType] = useState<string | null>(null);
  const sampleTypes = useSampleTypes();
  const [includeDisposed, setIncludeDisposed] = useState(false);
  const [dueForDisposal, setDueForDisposal] = useState(false);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);
  const mayShowVoided = can(PERMISSIONS.SAMPLES_VOID);
  const mayDecide = can(PERMISSIONS.SAMPLES_DECIDE);
  const showVoided = includeVoided && mayShowVoided;

  // The filters a group's "Show more samples" repeats, so the rest of a group
  // is the same selection as its first rows.
  const filterQuery: Record<string, string> = {
    order,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(sampleType ? { sampleType } : {}),
    ...(includeDisposed ? { includeDisposed: 'true' } : {}),
    ...(dueForDisposal ? { dueForDisposal: 'true' } : {}),
    ...(showVoided ? { includeVoided: 'true' } : {}),
  };
  const filterKey = { page, workOrderFilter, ...filterQuery };
  const samples = useQuery({
    queryKey: ['samples', filterKey],
    queryFn: () =>
      samplesApi.list({
        page,
        pageSize: GROUPS_PER_PAGE,
        pageBy: 'workOrder',
        groupSampleLimit: GROUP_SAMPLE_LIMIT,
        sort: 'workOrder',
        ...(workOrderFilter ? { workOrderId: workOrderFilter } : {}),
        ...filterQuery,
      }),
  });

  const isFiltered = Boolean(
    search || status || sampleType || includeDisposed || dueForDisposal || workOrderFilter || showVoided,
  );

  // Remembered per person in this browser (D323). A search, or one work order
  // chosen, opens every matching group instead, and what is pressed then is
  // kept only until that result changes.
  const storageKey = `lims.samples.openGroups:${user?.tenant?.slug ?? ''}:${user?.id ?? ''}`;
  const [remembered, setRemembered] = useState(() => readOpenGroups(storageKey));
  const opensMatches = Boolean(search || workOrderFilter);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const listKey = JSON.stringify(filterKey);
  useEffect(() => setOpened({}), [listKey]);
  const isGroupOpen = (key: string, index: number) =>
    opensMatches ? (opened[key] ?? true) : (remembered[key] ?? index === 0);
  const toggleGroup = (key: string, index: number) => {
    const next = !isGroupOpen(key, index);
    if (opensMatches) {
      setOpened((current) => ({ ...current, [key]: next }));
    } else {
      setRemembered(rememberOpenGroup(storageKey, key, next));
    }
  };
  const chooseWorkOrder = (id: string | null, number?: string) => {
    setSearchParams(id === null ? {} : number ? { workOrderId: id, job: number } : { workOrderId: id });
    setPage(1);
  };

  const columns: readonly DataColumn<Sample>[] = [
    {
      key: 'sampleNumber',
      header: t('samples.sampleId'),
      render: (sample) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          {/* A Sample ID is a machine identifier and what a scanner reads back:
              always left-to-right, in both languages, so its digits never
              reorder under Arabic. */}
          {/* The row opens its record through its identifier (D278). */}
          <Link
            to={`/samples/${sample.id}`}
            className={`measure font-semibold underline-offset-2 hover:text-primary-700 hover:underline ${
              sample.isVoided ? 'text-default-600 line-through' : 'text-default-900'
            }`}
            dir="ltr"
          >
            {sample.sampleNumber}
          </Link>
          {sample.isVoided ? (
            <StatusBadge label={t('sampleVoid.voidedChip')} tone="danger" size="sm" />
          ) : null}
          {sample.receipt ? (
            <Link
              to={`/samples/receipts/${sample.receipt.id}`}
              className="text-caption text-default-600 hover:text-primary-700 hover:underline"
              aria-label={t('receipts.viewNamed', { number: sample.receipt.receiptNumber })}
            >
              {t('receipts.receiptShort')}{' '}
              <span dir="ltr">{sample.receipt.receiptNumber}</span>
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      key: 'sampleType',
      header: t('samples.sampleTypeLabel'),
      render: (sample) => (
        <span className="text-default-700">
          {sampleTypes.nameOf(sample.sampleType)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('samples.statusLabel'),
      render: (sample) => (
        <div className="min-w-0">
          {/* Derived by the backend from the decision history. Rendered only. */}
          <StatusBadge
            label={t(`status.sample.${sample.status}`)}
            tone={toneForSampleStatus(sample.status)}
          />
          {sample.isDisposed ? (
            <span className="mt-1 block text-caption text-default-600">
              {t('samples.disposedBadge')}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'receivedAt',
      header: t('samples.receivedAt'),
      render: (sample) => (
        <div className="min-w-0">
          <span className="block text-default-700">
            {formatDate(sample.receivedAt, { locale })}
          </span>
          <span className="block truncate text-caption text-default-600">
            {sample.receivedFrom}
          </span>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('samples.quantity'),
      align: 'end',
      render: (sample) => (
        // The unit is the laboratory's own word for it and is shown with the
        // number, never assumed. A quantity without its unit is not a fact.
        <span className="text-default-800" dir="ltr">
          {formatNumber(sample.quantity, { locale })} {sample.quantityUnit}
        </span>
      ),
    },
    // No work order column: the group header names it once (D320).
    {
      key: 'pieces',
      header: t('samples.pieces'),
      align: 'end',
      render: (sample) => (
        // Zero pieces means nobody recorded any, which is not the same as the
        // sample being one piece. The column says the number it was told.
        <span className="text-default-700" dir="ltr">
          {formatNumber(sample.pieceCount, { locale })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (sample) =>
        // The next step where the sample file already offers it: a sample
        // awaiting inspection opens with the decision dialog, to whoever may
        // decide. The person still chooses; nothing is decided here.
        mayDecide && sample.status === 'awaiting_inspection' && !sample.isVoided && !sample.isDisposed ? (
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => navigate(`/samples/${sample.id}?decide=1`)}
            aria-label={t('samples.inspectNamed', { number: sample.sampleNumber })}
          >
            {t('samples.inspect')}
          </ActionButton>
        ) : (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => navigate(`/samples/${sample.id}`)}
          >
            {t('common.open')}
          </ActionButton>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('samples.title')}
        subtitle={t('samples.subtitle')}
        breadcrumbs={[{ label: t('nav.samples') }]}
        actions={
          can(PERMISSIONS.SAMPLES_RECEIVE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/samples/receive')}
            >
              {t('samples.receive')}
            </ActionButton>
          ) : null
        }
      />

      {/*
        The status views (D278): the same server-side status filter the
        register always had, as tabs, so "awaiting inspection" is one press
        away. No counts: the register is not asked for any, and none is made up.
      */}
      <StatusViews
        label={t('samples.statusViews')}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          { value: null, label: t('common.all') },
          ...Object.values(SAMPLE_STATUS).map((value) => ({
            value,
            label: t(`status.sample.${value}`),
          })),
        ]}
      />
      <FilterBar>
        {/*
          Autofocused, because a scan goes wherever the caret is. A technician
          holding a sample in one hand and a scanner in the other should not
          have to click first.
        */}
        <SearchField
          autoFocus
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label={t('samples.searchLabel')}
          placeholder={t('samples.searchPlaceholder')}
        />
        <SelectField
          label={t('samples.sampleTypeLabel')}
          value={sampleType}
          onChange={(value) => {
            setSampleType(value);
            setPage(1);
          }}
          placeholder={t('common.all')}
          isLoading={sampleTypes.isLoading}
          // Archived types stay filterable: their samples are still here.
          options={sampleTypes.types.map((type) => ({
            value: type.code,
            label: type.archived
              ? `${sampleTypes.nameOf(type.code)} (${t('sampleTypes.archivedTag')})`
              : sampleTypes.nameOf(type.code),
          }))}
        />
        {/*
          Retention is a laboratory's own policy and NOTHING acts on this list.
          It shows what is past its configured period so a person can decide;
          the system never disposes of anything on a schedule (decision D71).
        */}
        <CheckboxField
          label={t('samples.dueForDisposal')}
          description={t('samples.dueForDisposalHint')}
          value={dueForDisposal}
          onChange={(value) => {
            setDueForDisposal(value);
            setPage(1);
          }}
        />
        <CheckboxField
          label={t('samples.includeDisposed')}
          description={t('samples.includeDisposedHint')}
          value={includeDisposed}
          onChange={(value) => {
            setIncludeDisposed(value);
            setPage(1);
          }}
        />
        <WorkOrderFilter
          value={workOrderFilter}
          valueLabel={workOrderLabel}
          onChange={chooseWorkOrder}
        />
        <SegmentedControl
          label={t('samples.sortLabel')}
          value={order}
          onChange={(value) => {
            setOrder(value);
            setPage(1);
          }}
          options={[
            { value: 'desc', label: t('samples.sortNewest') },
            { value: 'asc', label: t('samples.sortOldest') },
          ]}
        />
        {mayShowVoided ? (
          <SwitchField
            label={t('sampleVoid.showDeleted')}
            description={t('sampleVoid.showDeletedHint')}
            value={includeVoided}
            onChange={(value) => {
              setIncludeVoided(value);
              setPage(1);
            }}
          />
        ) : null}
      </FilterBar>

      {samples.error ? (
        <ErrorState error={samples.error} onRetry={() => void samples.refetch()} />
      ) : samples.isLoading ? (
        <LoadingState />
      ) : (samples.data?.data.length ?? 0) === 0 ? (
        <Panel>
          <EmptyState title={isFiltered ? t('samples.noneFound') : t('samples.noneYet')} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-stack" role="list" aria-label={t('samples.groupsLabel')}>
          {groupRows(samples.data?.data ?? [], samples.data?.meta.workOrders ?? []).map(
            ({ group, rows }, index) => {
              const key = group.id ?? 'none';
              return (
                <WorkOrderGroup
                  key={key}
                  group={group}
                  rows={rows}
                  columns={columns}
                  filterQuery={filterQuery}
                  pageLimit={samples.data?.meta.groupSampleLimit ?? GROUP_SAMPLE_LIMIT}
                  isOpen={isGroupOpen(key, index)}
                  onToggle={() => toggleGroup(key, index)}
                />
              );
            },
          )}
          <PaginationNav
            page={page}
            totalPages={samples.data?.meta.totalPages ?? 1}
            onPageChange={setPage}
          />
        </div>
      )}
    </>
  );
}

/**
 * Cuts the server's order where the work order changes. Never re-sorts: the
 * order is the server's (D320), so a page and the next one always agree.
 */
function groupRows(rows: readonly Sample[], groups: readonly SampleWorkOrderGroup[]) {
  const result: { group: SampleWorkOrderGroup; rows: Sample[] }[] = [];
  for (const sample of rows) {
    const last = result[result.length - 1];
    if (last && last.group.id === sample.workOrderId) {
      last.rows.push(sample);
      continue;
    }
    const group = groups.find((entry) => entry.id === sample.workOrderId) ?? {
      id: sample.workOrderId,
      number: sample.jobNumber,
      clientName: null,
      projectName: null,
      createdAt: null,
      sampleCount: 0,
      statusCounts: {},
    };
    result.push({ group, rows: [sample] });
  }
  return result;
}

/**
 * The register's work order filter (D322): all, one work order, or the
 * samples with none. The list is this laboratory's work orders that have
 * samples, searched on the server as the person types.
 */
function WorkOrderFilter({
  value,
  valueLabel,
  onChange,
}: {
  readonly value: string | null;
  readonly valueLabel: string | null;
  readonly onChange: (id: string | null, number?: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  // One request per pause in typing, not one per key.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(text.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [text]);
  const found = useQuery({
    queryKey: ['sample-work-order-options', search],
    queryFn: () => samplesApi.workOrderOptions({ ...(search ? { search } : {}), limit: 20 }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const workOrders = found.data?.data ?? [];
  const options: SelectOption[] = [
    { value: ALL_WORK_ORDERS, label: t('samples.workOrderFilterAll') },
    { value: 'none', label: t('samples.workOrderFilterNone') },
    // A work order chosen from a link stays listed while other searches run.
    ...(value && value !== 'none' && !workOrders.some((row) => row.id === value)
      ? [{ value, label: valueLabel ?? t('samples.workOrderFilter') }]
      : []),
    ...workOrders.map((row) => {
      const parties = [row.clientName, row.projectName].filter(Boolean).join(' / ');
      return parties
        ? { value: row.id, label: row.number, description: parties }
        : { value: row.id, label: row.number };
    }),
  ];

  return (
    <SearchSelectField
      label={t('samples.workOrderFilter')}
      // "All" leaves the box empty, so its placeholder says what to type.
      value={value}
      onChange={(next) => {
        if (next === ALL_WORK_ORDERS) onChange(null);
        else if (next === 'none') onChange('none');
        else onChange(next, workOrders.find((row) => row.id === next)?.number ?? valueLabel ?? undefined);
      }}
      options={options}
      onSearchChange={setText}
      {...(valueLabel ? { selectedLabel: valueLabel } : {})}
      placeholder={t('samples.workOrderFilterPlaceholder')}
      emptyContent={t('samples.workOrderFilterEmpty')}
      isLoading={found.isFetching}
    />
  );
}

/** The filter's "all work orders" row; never a record id. */
const ALL_WORK_ORDERS = '__all__';

function WorkOrderGroup({
  group,
  rows: firstRows,
  columns,
  filterQuery,
  pageLimit,
  isOpen,
  onToggle,
}: {
  readonly group: SampleWorkOrderGroup;
  readonly rows: readonly Sample[];
  readonly columns: readonly DataColumn<Sample>[];
  readonly filterQuery: Record<string, string>;
  readonly pageLimit: number;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation();
  const bodyId = useId();
  const name = group.id ? (group.number ?? '') : t('samples.withoutWorkOrder');
  const parties = [group.clientName, group.projectName].filter(Boolean).join(' / ');
  // The rest of a large group (D321): the ordinary sample paging of this one
  // work order, in the same order, continuing where the register page stops.
  const [wantsMore, setWantsMore] = useState(false);
  const more = useInfiniteQuery({
    queryKey: ['samples', 'group-rest', group.id ?? 'none', pageLimit, filterQuery],
    queryFn: ({ pageParam }) =>
      samplesApi.list({
        workOrderId: group.id ?? 'none',
        page: pageParam,
        pageSize: pageLimit,
        ...filterQuery,
      }),
    initialPageParam: 2,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
    enabled: wantsMore,
  });
  const extra = more.data?.pages.flatMap((response) => response.data) ?? [];
  const rows = [
    ...firstRows,
    ...extra.filter((row) => !firstRows.some((first) => first.id === row.id)),
  ];
  const total = Math.max(group.sampleCount, rows.length);
  const hasMore = rows.length < total && (!more.data || more.hasNextPage);
  // In the statuses' own order, and only the ones that occur.
  const locale = currentLocale();
  const summary = [
    t('samples.groupSamples', { total: formatNumber(total, { locale }) }),
    ...Object.values(SAMPLE_STATUS)
      .filter((status) => (group.statusCounts[status] ?? 0) > 0)
      .map(
        (status) =>
          `${t(`status.sample.${status}`)}: ${formatNumber(group.statusCounts[status] ?? 0, { locale })}`,
      ),
  ];

  return (
    <section
      role="listitem"
      className="min-w-0 rounded-large border border-default-200 bg-white"
      data-testid="sample-group"
      data-work-order={group.id ?? 'none'}
    >
      <header className="flex min-w-0 items-start gap-2 px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          aria-label={t('samples.groupToggle', { name })}
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-medium text-default-700 hover:bg-default-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
        >
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`size-4 transition-transform ${isOpen ? '' : '-rotate-90 rtl:rotate-90'}`}
          >
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-body font-bold text-default-900">
            {group.id ? (
              <Link
                to={`/work-orders/${group.id}`}
                dir="ltr"
                className="underline-offset-2 hover:text-primary-700 hover:underline"
              >
                {name}
              </Link>
            ) : (
              <span>{name}</span>
            )}
            {parties ? (
              <span className="min-w-0 truncate font-normal text-default-700">{parties}</span>
            ) : null}
          </h2>
          <p className="text-caption text-default-600">
            {summary.join(' · ')}
            {rows.length < total
              ? ` · ${t('samples.groupShowing', {
                  shown: formatNumber(rows.length, { locale }),
                  total: formatNumber(total, { locale }),
                })}`
              : null}
          </p>
        </div>
      </header>
      <div id={bodyId} hidden={!isOpen} className="border-t border-default-200 px-1 pb-1 sm:px-2">
        {isOpen ? (
          <>
            <DataTable
              label={name}
              columns={columns}
              rows={rows}
              rowKey={(sample) => sample.id}
            />
            {hasMore ? (
              <div className="flex justify-center px-2 py-2">
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  isLoading={more.isFetching}
                  onPress={() => {
                    if (!wantsMore) setWantsMore(true);
                    else void more.fetchNextPage();
                  }}
                >
                  {t('samples.groupShowMore')}
                </ActionButton>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
